import store from '../store';
import { RootState } from '../store/rootReducer';
import { ContextDisplayStatus } from '../store/context/reducer';
import { getArSessionCandidateContributor } from './assessmentRunSessionService';

export enum TaskSectionFrameTypes {
    INTRODUCTORY = 'introductory',
    COMPULSORY = 'compulsory',
    ALTERNATIVE_COMPULSORY = 'alternative_compulsory',
    OPTIONAL = 'optional',
    PART_2_AND_3 = 'part 2/3',
    PART_2 = 'part 2',
    PART_3 = 'part 3',
}

export enum DefinedSectionId {
    PART_1 = 'part1',
    PART_2 = 'part2',
}

export interface SpeakingQuestionSetIds {
    part1SectionId: number;
    part2And3SectionId: number;
    part2QuestionId: number;
    part3QuestionId: number;
}
export const isIntroductoryFrame = (frame: any) => frame?.customFields
    ?.some((customField: any) => customField.value.toString().toLowerCase() === TaskSectionFrameTypes.INTRODUCTORY);

export const isCompulsoryFrame = (frame: any) => frame?.customFields
    ?.some((customField: any) => customField.value.toString().toLowerCase() === TaskSectionFrameTypes.COMPULSORY
        || isPart2AndPart3Frame(frame));

export const isOptionalFrame = (frame: any) => frame?.customFields
    ?.some((customField: any) => customField.value.toString().toLowerCase() === TaskSectionFrameTypes.OPTIONAL);

export const isPart2AndPart3Frame = (frame: any) => frame?.customFields
    ?.some((customField: any) => customField.value.toString().toLowerCase() === TaskSectionFrameTypes.PART_2_AND_3);

export const getFrameShortDescriptionField = (frame: any) => frame?.customFields
    ?.find((customField: any) => customField.name === 'Short description');

export const parseSpeakingQuestionSetIds = (questionSet: any): SpeakingQuestionSetIds => {
    const { sections } = questionSet;

    // parse sectionId for part 1
    const sectionForPart1 = sections.find((section: any) => {
        return section.sections.some((sectionEl: any) => isIntroductoryFrame(sectionEl));
    });

    const sectionForPart2And3 = sections.find((section: any) => {
        return section.sections.some((sectionEl: any) => isPart2AndPart3Frame(sectionEl));
    });

    const part2Question = sectionForPart2And3?.questions
        ?.find((speakingQuestion: any) => isPart2Question(speakingQuestion));

    const part3Question = sectionForPart2And3?.questions
        ?.find((speakingQuestion: any) => isPart3Question(speakingQuestion));

    return {
        part1SectionId: sectionForPart1?.id,
        part2And3SectionId: sectionForPart2And3?.id,
        part2QuestionId: part2Question?.contentRevisionId,
        part3QuestionId: part3Question?.contentRevisionId,
    };
};

export const isPart2Question = (question: any) => question?.customFields
    ?.some((customField: any) => customField.value.toString().toLowerCase() === TaskSectionFrameTypes.PART_2);

export const isPart3Question = (question: any) => question?.customFields
    ?.some((customField: any) => customField.value.toString().toLowerCase() === TaskSectionFrameTypes.PART_3);


export const isOnPart1Context = () => {
    const { context } = store.getState() as RootState;
    const { speakingQuestionSetIds, assessmentRunSession } = context;

    return assessmentRunSession.contextObjectId === speakingQuestionSetIds?.part1SectionId;
};

export const isOnPart2Context = () => {
    const { context } = store.getState() as RootState;
    const { speakingQuestionSetIds, assessmentRunSession } = context;

    return assessmentRunSession.contextObjectId === speakingQuestionSetIds.part2QuestionId;
};

export const isOnPart3Context = () => {
    const { context } = store.getState() as RootState;
    const { speakingQuestionSetIds, assessmentRunSession } = context;

    return assessmentRunSession.contextObjectId === speakingQuestionSetIds.part3QuestionId;
};

export const getStudentContextDisplayStatus = () => {
    const { context: { assessmentRunSession } } = store.getState() as RootState;
    const candidate = getArSessionCandidateContributor(assessmentRunSession);

    return candidate?.contextDisplayStatus;
};

export const isStudentTopicVisible = () => {
    const contextDisplayStatus = getStudentContextDisplayStatus();

    return contextDisplayStatus === ContextDisplayStatus.CONTEXT_DISPLAY_ACK;
};

export const isStudentTopicHidden = () => {
    const contextDisplayStatus = getStudentContextDisplayStatus();

    return !contextDisplayStatus || contextDisplayStatus === ContextDisplayStatus.CONTEXT_HIDE_ACK;
};

export const isContextDisplayStatusReqOrAck = (contextDisplayStatus: ContextDisplayStatus) => {
    return contextDisplayStatus === ContextDisplayStatus.CONTEXT_DISPLAY_REQ
        || contextDisplayStatus === ContextDisplayStatus.CONTEXT_DISPLAY_ACK;
};

export const isContextHideStatusReqOrAck = (contextDisplayStatus: ContextDisplayStatus) => {
    return contextDisplayStatus === ContextDisplayStatus.CONTEXT_HIDE_REQ
        || contextDisplayStatus === ContextDisplayStatus.CONTEXT_HIDE_ACK;
};

export const canUpdateContextDisplayStatus = () => {
    const contextDisplayStatus = getStudentContextDisplayStatus();

    return !contextDisplayStatus || isContextDisplayStatusReqOrAck(contextDisplayStatus);
};
